<template>
  <nav
    class="navbar"
    :class="{ 'navbar--scrolled': isScrolled, 'navbar--open': mobileOpen }"
    :style="navbarStyle"
  >
    <div class="navbar__inner">
      <div class="navbar__brand" @click="onNavigate('Home')">
        <img :src="logoSrc" alt="YWG" class="navbar__logo" />
        <span class="navbar__subtitle">广东省南方文化产权交易所官方平台</span>
      </div>

      <!-- 汉堡菜单按钮（仅移动端显示） -->
      <div class="navbar__burger" @click="mobileOpen = !mobileOpen">
        <span></span><span></span><span></span>
      </div>

      <!-- 菜单 -->
      <div class="navbar__menu" v-show="mobileOpen || !isMobile">
        <div
          v-for="item in menuItems"
          :key="item.name"
          class="navbar__menu-item"
          :class="{ 'navbar__menu-item--active': activeMenu === item.name }"
          @mouseenter="item.sub && (activeDropdown = item.name)"
          @mouseleave="activeDropdown = null"
          @click="onNavigate(item.name)"
        >
          {{ item.label }}
          <transition name="dropdown">
            <div
              v-if="item.sub && activeDropdown === item.name"
              class="navbar__dropdown"
            >
              <div
                v-for="(sub, idx) in item.sub"
                :key="idx"
                class="navbar__dropdown-item"
                :style="{ '--delay': idx * 0.06 + 's' }"
                @click.stop="onSubClick(sub)"
              >
                {{ sub.title }}
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import logoDark from "@/assets/images/YWG_logo03.png";
import logoLight from "@/assets/images/YWG_logo01.png";

export default {
  name: "NavBar",
  props: {
    mode: {
      type: String,
      default: "transparent",
      validator: (v) => ["transparent", "solid"].includes(v),
    },
    activeMenu: {
      type: String,
      default: "",
    },
    scrollThreshold: {
      type: Number,
      default: 200,
    },
    fixed: {
      type: Boolean,
      default: true,
    },
    menuItems: {
      type: Array,
      default: () => [
        { name: "Home", label: "首页" },
        { name: "bigScreen", label: "数据监控平台" },
        {
          name: "CulturalMall",
          label: "展会服务",
          sub: [
            {
              title: "展商专区",
              code: "CulturalMall",
              url: "matrix1",
            },
            {
              title: "交易专区",
              code: "CulturalMall",
              url: "matrix2",
            },
            {
              title: "活动专区",
              code: "CulturalMall",
              url: "matrix3",
            },
          ],
        },
        {
          name: "ArtTrade",
          label: "技术服务",
          sub: [
            {
              title: "版权服务",
              url: "https://www.shubanyi.com/index",
              code: "ArtTrade",
            },
            {
              title: "溯源服务",
              url: "https://chain.cnscee.com/cnscee/home",
              code: "ArtTrade",
            },
          ],
        },
        { name: "Gift", label: "广东礼物" },
        { name: "Heritage", label: "数遗光年" },
      ],
    },
  },
  data() {
    return {
      scrollY: 0,
      ticking: false,
      mobileOpen: false,
      isMobile: false,
      activeDropdown: null,
    };
  },
  computed: {
    isScrolled() {
      return this.scrollY >= this.scrollThreshold;
    },
    navbarStyle() {
      if (this.mode === "solid") {
        return {
          backgroundColor: "rgba(244, 242, 239, 1)",
          position: this.fixed ? "fixed" : "relative",
          "--nav-menu-color": "rgba(32,50,71,1)",
          "--nav-subtitle-color": "rgba(106,98,86,1)",
          "--nav-active-color": "rgba(30,90,168,1)",
          "--nav-burger-color": "rgba(32,50,71,1)",
          "--nav-active-weight": "700",
        };
      }

      const o = Math.min(this.scrollY / this.scrollThreshold, 1);

      if (this.isScrolled) {
        return {
          backgroundColor: "rgba(244, 242, 239, 1)",
          position: this.fixed ? "fixed" : "relative",
          "--nav-menu-color": "rgba(32,50,71,1)",
          "--nav-subtitle-color": "rgba(106,98,86,1)",
          "--nav-active-color": "rgba(30,90,168,1)",
          "--nav-burger-color": "rgba(32,50,71,1)",
          "--nav-active-weight": "700",
        };
      }

      return {
        backgroundColor: `rgba(244, 242, 239, ${o.toFixed(2)})`,
        position: this.fixed ? "fixed" : "relative",
        "--nav-menu-color": `rgba(255,255,255,${(1 + 0.25 * o).toFixed(2)})`,
        "--nav-subtitle-color": `rgba(255,255,255,${(1 + 0.3 * o).toFixed(2)})`,
        "--nav-active-color": "#ffffff",
        "--nav-burger-color": "#ffffff",
        "--nav-active-weight": "normal",
      };
    },
    logoSrc() {
      if (this.mode === "solid" || this.isMobile) return logoLight;
      return this.isScrolled ? logoLight : logoDark;
    },
  },
  watch: {
    $route() {
      this.mobileOpen = false;
      window.scrollTo(0, 0);
    },
  },
  mounted() {
    this.scrollY = window.pageYOffset;
    this.checkMobile();
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.checkMobile, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.scrollY = window.pageYOffset;
        this.ticking = false;
      });
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
      if (!this.isMobile) this.mobileOpen = false;
    },
    onNavigate(name) {
      this.mobileOpen = false;
      const item = this.menuItems.find((m) => m.name === name);
      if (item && item.sub) return;
      if (name === "bigScreen") {
        const baseUrl = "https://www.yuewengou.com";
        // const baseUrl =
        //   process.env.NODE_ENV === "production"
        //     ? "https://www.yuewengou.com"
        //     : "https://ywg-w.s136.ailo.vip";
        window.open(`${baseUrl}/bigScreen/`, "_blank");
        return;
      } else if (name === "Gift") {
        const baseUrl = "https://www.gdlw.cn";
        // const baseUrl =
        //   process.env.NODE_ENV === "production"
        //     ? "https://www.yuewengou.com"
        //     : "https://ywg-w.s136.ailo.vip";
        window.open(`${baseUrl}`, "_blank");
        return;
      }
      if (this.$route.name !== name) {
        this.$router.push({ name });
      }
    },
    onSubClick(sub) {
      this.activeDropdown = null;
      this.mobileOpen = false;
      if (sub.code == "ArtTrade") {
        window.open(sub.url, "_blank");
      } else if (sub.code == "CulturalMall") {
        const targetId = sub.url;
        if (!targetId) return;

        const doScroll = () => {
          setTimeout(() => {
            const el = document.getElementById(targetId);
            if (el) {
              const top =
                el.getBoundingClientRect().top + window.pageYOffset - 90;
              window.scrollTo({ top, behavior: "smooth" });
            }
          }, 300);
        };

        if (this.$route.path !== "/") {
          this.$router.push("/").then(doScroll);
        } else {
          doScroll();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.navbar {
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: $nav-height;
  transition: background-color 0.3s ease;
  max-width: 1720px;
  margin: 0 auto;

  &__inner {
    margin: 0 auto;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 30px;
    cursor: pointer;
  }

  &__logo {
    width: 166px;
    height: 49px;
    object-fit: contain;
  }

  &__subtitle {
    font-size: $font-size-small;
    white-space: nowrap;
    color: var(--nav-subtitle-color);
    transition: color 0.3s ease;
  }

  // ---- 汉堡按钮（PC 隐藏） ----
  &__burger {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 44px;
    height: 44px;
    cursor: pointer;

    span {
      display: block;
      width: 22px;
      height: 2px;
      border-radius: 1px;
      background: var(--nav-burger-color);
      transition: transform 0.3s ease, opacity 0.3s ease,
        background-color 0.3s ease;
    }
  }

  // ---- 菜单 ----
  &__menu {
    display: flex;
    align-items: center;
    gap: 60px;
  }

  &__menu-item {
    font-size: $font-size-nav;
    cursor: pointer;
    transition: opacity 0.2s ease, color 0.3s ease;
    white-space: nowrap;
    min-height: 44px;
    display: flex;
    align-items: center;
    position: relative;
    color: var(--nav-menu-color);

    &:hover {
      opacity: 1;
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(16, 42, 67, 0.9); // #102A43E6
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px 10px;
    min-width: 160px;
    z-index: 1001;
  }

  &__dropdown-item {
    height: 40px;
    line-height: 40px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: normal;
    color: rgba(255, 255, 255, 0.85); // #FFFFFFD9
    cursor: pointer;
    white-space: nowrap;
    border-radius: 6px;
    opacity: 0;
    transform: translateY(-8px);
    animation: dropdown-item-in 0.3s ease forwards;
    animation-delay: var(--delay, 0s);
    transition: color 0.2s ease, background-color 0.2s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.95); // #FFFFFFF2
      background-color: rgba(255, 255, 255, 0.2); // #ffffff33
    }
  }

  &__menu-item--active {
    color: var(--nav-active-color);
    font-weight: var(--nav-active-weight, normal);
  }

  // ---- 平板适配 ----
  @include tablet {
    .navbar__inner {
      padding: 0 32px;
    }

    .navbar__menu {
      gap: 32px;
    }
  }

  // ---- 手机适配 ----
  @include mobile {
    --nav-menu-color: rgba(32, 50, 71, 1) !important;
    --nav-subtitle-color: rgba(106, 98, 86, 1) !important;
    --nav-active-color: rgba(30, 90, 168, 1) !important;
    --nav-burger-color: rgba(32, 50, 71, 1) !important;
    --nav-active-weight: 700 !important;
    background-color: rgba(244, 242, 239, 1) !important;

    height: $nav-height-mobile;

    .navbar__inner {
      padding: 0 20px;
    }

    .navbar__brand {
      gap: 12px;
    }

    .navbar__logo {
      width: 120px;
      height: 35px;
    }

    .navbar__subtitle {
      display: none;
    }

    .navbar__burger {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .navbar__menu {
      position: absolute;
      top: $nav-height-mobile;
      left: 0;
      right: 0;
      flex-direction: column;
      gap: 0;
      padding: 8px 0;
      background-color: #f4f2ef;
    }

    .navbar__menu-item {
      width: 100%;
      justify-content: center;
      padding: 12px 20px;
      min-height: 44px;
    }
  }
}

@keyframes dropdown-item-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-6px);
}

.dropdown-enter-to,
.dropdown-leave {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
</style>
